import React, { useState } from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Grid, Chip, Paper, Button, CircularProgress, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import { Transaction } from './TransactionsTable';
import { useToast } from './ui/ToastProvider';
interface TransactionDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction;
  onAction?: (actionType: string, reason?: string) => Promise<void>;
}
const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({
  open,
  onClose,
  transaction,
  onAction
}) => {
  const [expandedState, setExpandedState] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    showToast
  } = useToast();
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  const getStatusColor = (status: string) => {
    const colors: Record<string, {
      bg: string;
      color: string;
    }> = {
      IN_PROGRESS: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6'
      },
      SUCCESS: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981'
      },
      FAILED: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444'
      },
      IN_REVIEW: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      },
      PENDING_INQUIRY: {
        bg: 'rgba(139, 92, 246, 0.1)',
        color: '#8B5CF6'
      },
      PASS: {
        bg: 'rgba(16, 185, 129, 0.1)',
        color: '#10B981'
      },
      FAIL: {
        bg: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444'
      },
      PENDING: {
        bg: 'rgba(59, 130, 246, 0.1)',
        color: '#3B82F6'
      },
      REVIEW: {
        bg: 'rgba(245, 158, 11, 0.1)',
        color: '#F59E0B'
      }
    };
    return colors[status] || {
      bg: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B'
    };
  };
  const getStateIcon = (stateCode: string) => {
    const icons: Record<string, string> = {
      SCREENING: 'lucide:shield-check',
      TITLE_FETCH: 'lucide:id-card',
      RAAST_TITLE_FETCH: 'lucide:id-card',
      ONELINK_TITLE_FETCH: 'lucide:id-card',
      BALANCE_CHECK: 'lucide:building-2',
      RTGS_BALANCE_CHECK: 'lucide:building-2',
      FT_POST: 'lucide:send',
      RAAST_POST: 'lucide:send',
      ONELINK_POST: 'lucide:send',
      RTGS_POST: 'lucide:send',
      SUCCESS: 'lucide:check-circle',
      FAILED: 'lucide:x-circle'
    };
    return icons[stateCode] || 'lucide:circle';
  };
  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      PASS: 'lucide:check',
      FAIL: 'lucide:x',
      PENDING: 'lucide:clock',
      REVIEW: 'lucide:alert-triangle'
    };
    return icons[status] || 'lucide:help-circle';
  };
  const handleAction = async (actionType: string) => {
    if (!onAction) return;
    setActionLoading(true);
    try {
      await onAction(actionType);
      showToast(`${actionType} completed successfully`, 'success');
    } catch (error) {
      showToast(`${actionType} failed`, 'error');
    } finally {
      setActionLoading(false);
    }
  };
  const renderOverviewAccordion = () => <Accordion defaultExpanded data-id="element-2515" data-mp="gso4jyup2s32">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2517" data-mp="brqllk1v1u0i" />} data-id="element-2516" data-mp="bqgniuv0cltv">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2518" data-mp="7qewdfmwfasu">
          <Icon icon="lucide:info" width={20} color="#0EA5E9" data-id="element-2519" data-mp="264gjecvcnnd6" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2520" data-mp="223eizspp559m">
            Overview
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2521" data-mp="297nnmc38gzrk">
        <Grid container spacing={2} data-id="element-2522" data-mp="2e5xkqs8iqaft">
          <Grid item xs={12} sm={6} data-id="element-2523" data-mp="2e4w9amnlblxk">
            <Typography variant="caption" color="text.secondary" data-id="element-2524" data-mp="2e3uxuh2nwxfb">
              Transaction ID
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-2525" data-mp="247rlyji0dkf6">
              {transaction.id}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2526" data-mp="1qg392dzxqrx5">
            <Typography variant="caption" color="text.secondary" data-id="element-2527" data-mp="1qf1xm8f0c3ew">
              Bank Reference
            </Typography>
            <Typography variant="body1" fontWeight={600} data-id="element-2528" data-mp="1v8wgxaqpagdg">
              {transaction.bankRef}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2529" data-mp="1lgiba6wi4pm0">
            <Typography variant="caption" color="text.secondary" data-id="element-2530" data-mp="1lhtheddf69ga">
              Created At
            </Typography>
            <Typography variant="body2" data-id="element-2531" data-mp="17jlkg5gdv854">
              {formatDate(transaction.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2532" data-mp="suk11kwtcaj1">
            <Typography variant="caption" color="text.secondary" data-id="element-2533" data-mp="svlchqhqqz1a">
              Last Updated
            </Typography>
            <Typography variant="body2" data-id="element-2534" data-mp="nxjpwvl3whl2">
              {formatDate(transaction.updatedAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2535" data-mp="xu4wvmgos2rj">
            <Typography variant="caption" color="text.secondary" data-id="element-2536" data-mp="xstwzxqd3f5p">
              Current State
            </Typography>
            <Chip label={transaction.currentState.replace(/_/g, ' ')} size="small" sx={{
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            color: '#64748B',
            fontWeight: 600,
            fontSize: '0.7rem',
            mt: 0.5
          }} data-id="element-2537" data-mp="1bs366dg5h906" />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderPartiesAccordion = () => <Accordion data-id="element-2538" data-mp="1plu9h43afwng">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2540" data-mp="1pj81528kpd46" />} data-id="element-2539" data-mp="1pmdq8hh7cezw">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2541" data-mp="n8xyxu4okyqu">
          <Icon icon="lucide:users" width={20} color="#0EA5E9" data-id="element-2542" data-mp="s4dbmiqycolo" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2543" data-mp="w7jinslw2jt7">
            Parties
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2544" data-mp="110w0njw2bscz">
        <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2545" data-mp="15ynuwkk6jqkc">
          Remitter
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        mb: 3,
        borderRadius: 2
      }} data-id="element-2546" data-mp="1aw66kipl0jw1">
          <Typography variant="body1" fontWeight={600} data-id="element-2547" data-mp="1fuhg0sbyssdq">
            {transaction.remitterName}
          </Typography>
        </Paper>
        <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2548" data-mp="3ho6w9bp6qcdv">
          Beneficiary
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        borderRadius: 2
      }} data-id="element-2549" data-mp="3lqboq73wd2yx">
          <Typography variant="body1" fontWeight={600} data-id="element-2550" data-mp="3e72iunulnmem">
            {transaction.beneficiaryName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
          mt: 1
        }} data-id="element-2551" data-mp="34fy15gdjhng3">
            IBAN: {transaction.beneficiaryIBAN}
          </Typography>
        </Paper>
      </AccordionDetails>
    </Accordion>;
  const renderAccountsAccordion = () => <Accordion data-id="element-2552" data-mp="3s50tpj9pp0pd">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2554" data-mp="3s6bynmew8ei2" />} data-id="element-2553" data-mp="3s6lnv7s46hmp">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2555" data-mp="3o2w1moywwc4u">
          <Icon icon="lucide:building-2" width={20} color="#6366F1" data-id="element-2556" data-mp="3j6ykigftr1r0" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2557" data-mp="2cpjti1w4d7cv">
            Accounts
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2558" data-mp="27s1ub37wm6i2">
        <Typography variant="subtitle2" gutterBottom data-id="element-2559" data-mp="bm15c0cuz3k4">
          Beneficiary Account
        </Typography>
        <Paper variant="outlined" sx={{
        p: 2,
        borderRadius: 2
      }} data-id="element-2560" data-mp="fonrplqrfw9x">
          <Grid container spacing={2} data-id="element-2561" data-mp="1poa2y1o0hn9">
            <Grid item xs={12} data-id="element-2562" data-mp="1qplj3mlf65i">
              <Typography variant="caption" color="text.secondary" data-id="element-2563" data-mp="6mo8cms9wg3t">
                IBAN
              </Typography>
              <Typography variant="body2" fontFamily="monospace" data-id="element-2564" data-mp="ued5cgg1hsr3">
                {transaction.beneficiaryIBAN}
              </Typography>
            </Grid>
            <Grid item xs={12} data-id="element-2565" data-mp="kn8tvqpkoq10">
              <Typography variant="caption" color="text.secondary" data-id="element-2566" data-mp="km7fpdd9fnf2">
                Account Title
              </Typography>
              <Typography variant="body2" data-id="element-2567" data-mp="pkqzb74dsn49">
                {transaction.beneficiaryName}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>;
  const renderAmountsAccordion = () => <Accordion data-id="element-2568" data-mp="32sry5d75pn46">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2570" data-mp="32r77vzrvkqc4" />} data-id="element-2569" data-mp="32s8hdzt8t4rq">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2571" data-mp="2xuqfuuhk1rtr">
          <Icon icon="lucide:banknote" width={20} color="#10B981" data-id="element-2572" data-mp="3bkk69h8pkk5l" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2573" data-mp="37ktat088lfuq">
            Amounts & FX
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2574" data-mp="2jw0lvnq1889m">
        <Grid container spacing={2} data-id="element-2575" data-mp="2evckq983ab1o">
          <Grid item xs={12} sm={6} data-id="element-2576" data-mp="2ewe6b7mqefxn">
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2577" data-mp="2exflnoas5ol6">
              <Typography variant="caption" color="text.secondary" data-id="element-2578" data-mp="2nuzoh1oajsxw">
                Foreign Currency Amount
              </Typography>
              <Typography variant="h6" color="primary.main" data-id="element-2579" data-mp="86g4as0t64hd">
                {transaction.currencyFCY} {transaction.amountFCY}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} data-id="element-2580" data-mp="2dadf5jjpuhmq">
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2581" data-mp="288o25ycxntw0">
              <Typography variant="caption" color="text.secondary" data-id="element-2582" data-mp="23gd2mwm3ua3z">
                PKR Amount
              </Typography>
              <Typography variant="h6" color="primary.main" data-id="element-2583" data-mp="1uhrmt980p96w">
                PKR {transaction.amountPKR}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} data-id="element-2584" data-mp="1phddlnuojt7a">
            <Typography variant="caption" color="text.secondary" data-id="element-2585" data-mp="1kjlnmzdlih5q">
              Purpose
            </Typography>
            <Typography variant="body1" data-id="element-2586" data-mp="1gi9uijvijdss">
              {transaction.purposeText} ({transaction.purposeCode})
            </Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderComplianceAccordion = () => <Accordion data-id="element-2587" data-mp="3awj8z4eklk7r">
      <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2589" data-mp="wos2yciiu1gt" />} data-id="element-2588" data-mp="3atwwqrgqigj7">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-2590" data-mp="wm5ri1sif1tb">
          <Icon icon="lucide:shield-check" width={20} color="#F59E0B" data-id="element-2591" data-mp="16djq87cmzxle" />
          <Typography variant="subtitle2" fontWeight={600} data-id="element-2592" data-mp="16gfoxxwb6qaw">
            Screening Summary
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails data-id="element-2593" data-mp="dqpi89x0aqot">
        <Grid container spacing={2} data-id="element-2594" data-mp="dr8nor5gzmly">
          <Grid item xs={12} data-id="element-2595" data-mp="io7f31xr1fgg">
            <Typography variant="subtitle2" gutterBottom data-id="element-2596" data-mp="ipinxg61xdef">
              Screening Status
            </Typography>
            <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2597" data-mp="moj1aj5rfnu1">
              {transaction.stateHistory.find(s => s.stateCode === 'SCREENING') ? <>
                  <Chip icon={<Icon icon={getStatusIcon(transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.status)} width={14} data-id="element-2599" data-mp="31t73vzezjzl" />} label={transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.status} size="small" sx={{
                ...getStatusColor(transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.status),
                fontWeight: 600
              }} data-id="element-2598" data-mp="232rcjhl2yaqd" />
                  <Typography variant="body2" color="text.secondary" sx={{
                mt: 1
              }} data-id="element-2600" data-mp="2srodlp6e0sad">
                    Completed:{' '}
                    {formatDate(transaction.stateHistory.find(s => s.stateCode === 'SCREENING')!.timestamp)}
                  </Typography>
                </> : <Typography variant="body2" color="text.secondary" data-id="element-2601" data-mp="1qzbz02phed7k">
                  Not yet screened
                </Typography>}
            </Paper>
          </Grid>
          {transaction.ePRCNo && <Grid item xs={12} data-id="element-2602" data-mp="1hy47c3hh0amg">
              <Typography variant="subtitle2" gutterBottom data-id="element-2603" data-mp="1i19uveagb4g2">
                e-PRC Details
              </Typography>
              <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2
          }} data-id="element-2604" data-mp="6k0bssv8iefa">
                <Typography variant="body2" data-id="element-2605" data-mp="bkenckw1o6i2">
                  <strong data-id="element-2606" data-mp="qt9xdned83qg">e-PRC Number:</strong> {transaction.ePRCNo}
                </Typography>
                {transaction.ePRCDate && <Typography variant="body2" sx={{
              mt: 1
            }} data-id="element-2607" data-mp="m0gul52gj5h0">
                    <strong data-id="element-2608" data-mp="d24tg93yspab">Issue Date:</strong>{' '}
                    {formatDate(transaction.ePRCDate)}
                  </Typography>}
              </Paper>
            </Grid>}
        </Grid>
      </AccordionDetails>
    </Accordion>;
  const renderTimelineAccordion = () => {
    if (transaction.stateHistory.length === 0) {
      return <Accordion data-id="element-2609" data-mp="vrtmfwo99vn1">
          <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2611" data-mp="vrbe41objqyr" />} data-id="element-2610" data-mp="vsctujga51mt">
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-2612" data-mp="3lhf0zwqmimph">
              <Icon icon="lucide:clock" width={20} color="#6366F1" data-id="element-2613" data-mp="1w002evrm22p0" />
              <Typography variant="subtitle2" fontWeight={600} data-id="element-2614" data-mp="1w1uls5n8hmqz">
                Timeline
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails data-id="element-2615" data-mp="250fznnhrgdlf">
            <Box sx={{
            py: 4,
            textAlign: 'center'
          }} data-id="element-2616" data-mp="24z51c3733y1p">
              <Icon icon="lucide:clock" width={48} height={48} color="#94A3B8" data-id="element-2617" data-mp="29t99gps36c4d" />
              <Typography variant="body1" color="text.secondary" sx={{
              mt: 2
            }} data-id="element-2618" data-mp="2joatj77i8o03">
                No state history available
              </Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-2619" data-mp="uany1r2m2mrp">
                The transaction has not progressed through any states yet
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>;
    }
    return <Accordion data-id="element-2620" data-mp="21now3b0jnfo1">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2622" data-mp="1wolphm4x11dy" />} data-id="element-2621" data-mp="21p9hob4vro9r">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-2623" data-mp="1wqeyv96wxzpn">
            <Icon icon="lucide:clock" width={20} color="#6366F1" data-id="element-2624" data-mp="26l6wgd0hwmkf" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-2625" data-mp="2klrgtdm26qg1">
              Timeline
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-2626" data-mp="2fmeuctz3nldz">
          <Box sx={{
          position: 'relative'
        }} data-id="element-2627" data-mp="2fl3qku5nmdmv">
            {/* Timeline line */}
            <Box sx={{
            position: 'absolute',
            left: 16,
            top: 24,
            bottom: 24,
            width: 2,
            background: 'linear-gradient(180deg, #22D3EE 0%, #6366F1 100%)',
            opacity: 0.3
          }} data-id="element-2628" data-mp="2pc7v0zxpeg3g" />
            {transaction.stateHistory.map((state, index) => {
            const isExpanded = expandedState === `${state.stateCode}-${index}`;
            const statusColors = getStatusColor(state.status);
            return <Box key={`${state.stateCode}-${index}`} sx={{
              position: 'relative',
              mb: 3
            }} data-id="element-2629" data-mp="390m4sf7ct2wv" data-mpi={index}>
                  {/* Timeline node */}
                  <Box sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: statusColors.color,
                border: '3px solid white',
                boxShadow: `0 0 0 2px ${statusColors.color}`,
                zIndex: 1
              }} data-id="element-2630" data-mp="33zq1o3uttksi" />
                  <Accordion expanded={isExpanded} onChange={() => setExpandedState(isExpanded ? null : `${state.stateCode}-${index}`)} sx={{
                ml: 5
              }} data-id="element-2631" data-mp="30px6ya0taxn8">
                    <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={16} data-id="element-2633" data-mp="2htxc8rgg9fzy" />} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(79, 70, 229, 0.04)'
                  }
                }} data-id="element-2632" data-mp="2hrud9hxbkcj3">
                      <Box sx={{
                    width: '100%'
                  }} data-id="element-2634" data-mp="231dcm0sidqn9">
                        <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1
                    }} data-id="element-2635" data-mp="1u3kwiueyqeyt">
                          <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }} data-id="element-2636" data-mp="imgt38oalvpb">
                            <Icon icon={getStateIcon(state.stateCode)} width={20} height={20} color={statusColors.color} data-id="element-2637" data-mp="3r0h44bjqyszy" />
                            <Typography variant="subtitle2" fontWeight={600} data-id="element-2638" data-mp="4n8v2j3v8tmp">
                              {state.stateCode.replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                          <Chip icon={<Icon icon={getStatusIcon(state.status)} width={12} data-id="element-2640" data-mp="2fr4856r4f4c2" />} label={state.status} size="small" sx={{
                        backgroundColor: statusColors.bg,
                        color: statusColors.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 22
                      }} data-id="element-2639" data-mp="2fpbaui2edwgq" />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" data-id="element-2641" data-mp="2arj80t07spxr">
                          {formatDate(state.timestamp)}
                        </Typography>
                        {state.failReasonCode && <Typography variant="body2" color="error.main" sx={{
                      mt: 1
                    }} data-id="element-2642" data-mp="15r1rcouaot26">
                            {state.failReasonCode.replace(/_/g, ' ')}
                          </Typography>}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails data-id="element-2643" data-mp="myz9592094pt">
                      <Grid container spacing={2} data-id="element-2644" data-mp="i1gxvc0imden">
                        {state.primaryRef && <Grid item xs={12} sm={6} data-id="element-2645" data-mp="d2e4iotwdvmi">
                            <Typography variant="caption" color="text.secondary" data-id="element-2646" data-mp="d3xjmc3ti0mp">
                              Primary Reference
                            </Typography>
                            <Typography variant="body2" fontFamily="monospace" data-id="element-2647" data-mp="3plgt7gtv4l0a">
                              {state.primaryRef}
                            </Typography>
                          </Grid>}
                        {state.durationMs !== undefined && <Grid item xs={12} sm={6} data-id="element-2648" data-mp="3lk3l103as5q7">
                            <Typography variant="caption" color="text.secondary" data-id="element-2649" data-mp="3lkwsy4dzt19x">
                              Duration
                            </Typography>
                            <Typography variant="body2" data-id="element-2650" data-mp="3fa3yxetfqsu">
                              {formatDuration(state.durationMs)}
                            </Typography>
                          </Grid>}
                        {state.attempts && <Grid item xs={12} sm={6} data-id="element-2651" data-mp="w1csgie4iqfi">
                            <Typography variant="caption" color="text.secondary" data-id="element-2652" data-mp="10v7h808l5vlg">
                              Attempts
                            </Typography>
                            <Typography variant="body2" data-id="element-2653" data-mp="165jn01hi6ojy">
                              {state.attempts}
                            </Typography>
                          </Grid>}
                        {state.requestSnippet && <Grid item xs={12} data-id="element-2654" data-mp="ravaw4hlkrvd">
                            <Typography variant="caption" color="text.secondary" data-id="element-2655" data-mp="rbww31sbuuqt">
                              Request
                            </Typography>
                            <Paper variant="outlined" sx={{
                        p: 1,
                        mt: 0.5,
                        backgroundColor: '#F8FAFC',
                        overflow: 'auto'
                      }} data-id="element-2656" data-mp="w8f01zfu2p23">
                              <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }} data-id="element-2657" data-mp="m5t6lwpzmoat">
                                {state.requestSnippet}
                              </Typography>
                            </Paper>
                          </Grid>}
                        {state.responseSnippet && <Grid item xs={12} data-id="element-2658" data-mp="1iuipnqdcpmyd">
                            <Typography variant="caption" color="text.secondary" data-id="element-2659" data-mp="1nuwyvbqov2xz">
                              Response
                            </Typography>
                            <Paper variant="outlined" sx={{
                        p: 1,
                        mt: 0.5,
                        backgroundColor: '#F8FAFC',
                        overflow: 'auto'
                      }} data-id="element-2660" data-mp="33gqt47x5ualj">
                              <Typography variant="body2" component="pre" sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all'
                        }} data-id="element-2661" data-mp="3n359bt5etyt3">
                                {state.responseSnippet}
                              </Typography>
                            </Paper>
                          </Grid>}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Box>;
          })}
          </Box>
        </AccordionDetails>
      </Accordion>;
  };
  const renderAmendmentHistoryAccordion = () => {
    const hasAmendments = transaction.amendmentHistory && transaction.amendmentHistory.length > 0;
    const hasManualComment = transaction.manualActionComment;
    if (!hasAmendments && !hasManualComment) return null;
    return <Accordion data-id="element-2662" data-mp="539l1uj7az39">
        <AccordionSummary expandIcon={<Icon icon="lucide:chevron-down" width={20} data-id="element-2664" data-mp="1venm7xvef6y8" />} data-id="element-2663" data-mp="54u9r3iu3nt7">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-2665" data-mp="1vdcoaeomwtf1">
            <Icon icon="lucide:history" width={20} color="#F59E0B" data-id="element-2666" data-mp="3jzuum00xvrcu" />
            <Typography variant="subtitle2" fontWeight={600} data-id="element-2667" data-mp="3fxhr7i9o05j8">
              Amendment & Action History
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails data-id="element-2668" data-mp="3tqpj7b1nr31g">
          {hasManualComment && <Box sx={{
          mb: hasAmendments ? 4 : 0
        }} data-id="element-2669" data-mp="3ox4gx6t17mwg">
              <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2670" data-mp="7fv2vkszi3sl">
                Manual Action Comment
              </Typography>
              <Paper variant="outlined" sx={{
            p: 2,
            borderRadius: 2,
            borderLeft: `4px solid ${transaction.manualActionComment!.actionType === 'MANUAL_FAIL' ? '#EF4444' : '#10B981'}`
          }} data-id="element-2671" data-mp="ghujwd4017vj">
                <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-2672" data-mp="2damluuzbyhzg">
                  <Chip label={transaction.manualActionComment!.actionType.replace('_', ' ')} size="small" sx={{
                bgcolor: transaction.manualActionComment!.actionType === 'MANUAL_FAIL' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                color: transaction.manualActionComment!.actionType === 'MANUAL_FAIL' ? '#EF4444' : '#10B981',
                fontWeight: 600,
                fontSize: '0.7rem'
              }} data-id="element-2673" data-mp="1wfs7sl7vaidh" />
                  <Typography variant="caption" color="text.secondary" data-id="element-2674" data-mp="2ythcyi2a89aa">
                    {formatDate(transaction.manualActionComment!.timestamp)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{
              mt: 1,
              fontStyle: 'italic'
            }} data-id="element-2675" data-mp="i9ng6ohksszp">
                  "{transaction.manualActionComment!.comment}"
                </Typography>
              </Paper>
            </Box>}

          {hasAmendments && <Box data-id="element-2676" data-mp="3vmpgf8cpegk4">
              <Typography variant="subtitle2" color="primary.main" gutterBottom data-id="element-2677" data-mp="3vm7b7m82cs02">
                Amendment History
              </Typography>
              <Box sx={{
            position: 'relative'
          }} data-id="element-2678" data-mp="3qq8gzi020frq">
                <Box sx={{
              position: 'absolute',
              left: 16,
              top: 24,
              bottom: 24,
              width: 2,
              background: '#E2E8F0'
            }} data-id="element-2679" data-mp="1owsp6hs8cuv6" />

                {transaction.amendmentHistory!.map((amend, index) => <Box key={index} sx={{
              position: 'relative',
              mb: 3
            }} data-id="element-2680" data-mp="26ohe2gsv72fs" data-mpi={index}>
                    <Box sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                width: 18,
                height: 18,
                borderRadius: '50%',
                backgroundColor: amend.status === 'APPROVED' ? '#10B981' : amend.status === 'REJECTED' ? '#EF4444' : '#F59E0B',
                border: '3px solid white',
                boxShadow: '0 0 0 1px #E2E8F0',
                zIndex: 1
              }} data-id="element-2681" data-mp="2gfc29qpo8c3v" />

                    <Paper variant="outlined" sx={{
                ml: 5,
                p: 2,
                borderRadius: 2
              }} data-id="element-2682" data-mp="3lltuf76sn5r8">
                      <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2
                }} data-id="element-2683" data-mp="3bofoi1ihxzd6">
                        <Box data-id="element-2684" data-mp="3qker4wmu77ax">
                          <Chip label={amend.status} size="small" sx={{
                      bgcolor: amend.status === 'APPROVED' ? 'rgba(16, 185, 129, 0.1)' : amend.status === 'REJECTED' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                      color: amend.status === 'APPROVED' ? '#10B981' : amend.status === 'REJECTED' ? '#EF4444' : '#F59E0B',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      mb: 1
                    }} data-id="element-2685" data-mp="3qjdieytamwwd" />
                          <Typography variant="caption" color="text.secondary" display="block" data-id="element-2686" data-mp="904tw3cwnepf">
                            Requested by {amend.requestedBy} on{' '}
                            {formatDate(amend.timestamp)}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2} sx={{
                  mb: amend.checkerComment ? 2 : 0
                }} data-id="element-2687" data-mp="3l59612r1t311">
                        <Grid item xs={12} sm={6} data-id="element-2688" data-mp="3v3ge0w3d0aqw">
                          <Typography variant="caption" color="text.secondary" data-id="element-2689" data-mp="2xgq02tvh12c4">
                            Original Account
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary'
                    }} data-id="element-2690" data-mp="32d6q5tm8dqru">
                            {amend.oldAccount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} data-id="element-2691" data-mp="p9mhguu5amb2">
                          <Typography variant="caption" color="text.secondary" data-id="element-2692" data-mp="k9i072lexnjs">
                            New Account
                          </Typography>
                          <Typography variant="body2" fontFamily="monospace" fontWeight={600} color="primary.main" data-id="element-2693" data-mp="yzz0gimba7f3">
                            {amend.newAccount}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} data-id="element-2694" data-mp="1hxibtsw8s24o">
                          <Typography variant="caption" color="text.secondary" data-id="element-2695" data-mp="1hwgxnfjxiziq">
                            Fetched Title
                          </Typography>
                          <Typography variant="body2" fontWeight={500} data-id="element-2696" data-mp="3jpoe4sba3n67">
                            {amend.fetchedTitle}
                          </Typography>
                        </Grid>
                      </Grid>

                      {amend.checkerComment && <Box sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: '#F8FAFC',
                  borderRadius: 1
                }} data-id="element-2697" data-mp="1z6b6eyfxo6rr">
                          <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-2698" data-mp="1peeuajh0ig4j">
                            Checker Comment
                          </Typography>
                          <Typography variant="body2" sx={{
                    mt: 0.5
                  }} data-id="element-2699" data-mp="11kx2c7rzw7p3">
                            {amend.checkerComment}
                          </Typography>
                        </Box>}
                    </Paper>
                  </Box>)}
              </Box>
            </Box>}
        </AccordionDetails>
      </Accordion>;
  };
  const renderActionButtons = () => {
    const buttons = [];
    if (transaction.lifecycleStatus === 'IN_REVIEW' && transaction.currentState === 'SCREENING') {
      buttons.push(<Button key="approve" variant="contained" color="success" startIcon={<Icon icon="lucide:check" width={16} data-id="element-2701" data-mp="3d6fkldbgopb4" />} onClick={() => handleAction('approve')} disabled={actionLoading} data-id="element-2700" data-mp="3i4zbls4wwrac">
          Approve
        </Button>, <Button key="reject" variant="outlined" color="error" startIcon={<Icon icon="lucide:x" width={16} data-id="element-2703" data-mp="4qcgtingzks2" />} onClick={() => handleAction('reject')} disabled={actionLoading} data-id="element-2702" data-mp="34du7acqlnato">
          Reject
        </Button>);
    }
    if (transaction.lifecycleStatus === 'PENDING_INQUIRY') {
      buttons.push(<Button key="inquiry" variant="contained" startIcon={actionLoading ? <CircularProgress size={16} data-id="element-2705" data-mp="1rn25yibzx4mr" /> : <Icon icon="lucide:search" width={16} data-id="element-2706" data-mp="1rlirmx9wh3nm" />} onClick={() => handleAction('inquiry')} disabled={actionLoading} data-id="element-2704" data-mp="1io8c2zq1uscw">
          Run Inquiry
        </Button>);
    }
    if (transaction.lifecycleStatus === 'SUCCESS') {
      buttons.push(<Button key="download-eprc" variant="outlined" startIcon={<Icon icon="lucide:download" width={16} data-id="element-2708" data-mp="2bccmfyjkftlx" />} onClick={() => handleAction('download-eprc')} disabled={actionLoading} data-id="element-2707" data-mp="26iry6y7rydzx">
          Download e-PRC
        </Button>, <Button key="reverse" variant="outlined" color="error" startIcon={<Icon icon="lucide:undo-2" width={16} data-id="element-2710" data-mp="2pbm3t1jictrh" />} onClick={() => handleAction('reverse')} disabled={actionLoading} data-id="element-2709" data-mp="2kcsqb94812rb">
          Reverse
        </Button>);
    }
    return buttons;
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : 560,
      maxWidth: '100%'
    }
  }} data-id="element-2711" data-mp="1vaoneczjphgf">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-2712" data-mp="254np5avpiz7u">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2
      }} data-id="element-2713" data-mp="1cj5k051m01dv">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2
        }} data-id="element-2714" data-mp="1q9r41x2suhdc">
            <Typography variant="h6" data-id="element-2715" data-mp="xsesyz7ud6bt">Transaction Details</Typography>
            <IconButton onClick={onClose} size="small" data-id="element-2716" data-mp="trl525uefrix">
              <Icon icon="lucide:x" width={20} data-id="element-2717" data-mp="tr3bjgz4frep" />
            </IconButton>
          </Box>
          {/* Header Chips */}
          <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2
        }} data-id="element-2718" data-mp="jllbntmnedh1">
            <Chip label={transaction.payoutType} size="small" sx={{
            backgroundColor: transaction.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(139, 92, 246, 0.1)',
            color: transaction.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
            fontWeight: 600
          }} data-id="element-2719" data-mp="9qtbci1olcik" />
            <Chip label={transaction.lifecycleStatus.replace(/_/g, ' ')} size="small" sx={{
            ...getStatusColor(transaction.lifecycleStatus),
            fontWeight: 600
          }} data-id="element-2720" data-mp="si2px5qb6rh0" />
            <Chip label={`${transaction.currencyFCY} ${transaction.amountFCY}`} size="small" sx={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: '#10B981',
            fontWeight: 600
          }} data-id="element-2721" data-mp="3i63apie6s3mn" />
            <Chip label={transaction.corridor} size="small" sx={{
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            color: '#6366F1',
            fontWeight: 600
          }} data-id="element-2722" data-mp="1h4kouqr2jf8i" />
          </Box>
          {/* Subheader */}
          <Box data-id="element-2723" data-mp="2dt0f6p205smo">
            <Typography variant="subtitle2" color="text.secondary" gutterBottom data-id="element-2724" data-mp="2dsqxrpv0wtsl">
              Remitting Institute (MTO)
            </Typography>
            {transaction.mtoName ? <Typography variant="h6" color="primary.main" gutterBottom data-id="element-2725" data-mp="24raq3pvlf5s2">
                {transaction.mtoName}
              </Typography> : <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1
          }} data-id="element-2726" data-mp="te88qslo6c6u">
                <Typography variant="h6" color="text.secondary" data-id="element-2727" data-mp="gvme2j1xxehb">
                  —
                </Typography>
                <Chip label="No MTO provided" size="small" sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              fontWeight: 600
            }} data-id="element-2728" data-mp="utt2l8l0224b" />
              </Box>}
            <Typography variant="body2" color="text.secondary" data-id="element-2729" data-mp="3teb3l2w3m2ck">
              MTO Ref: {transaction.mtoRef} • Bank Ref: {transaction.bankRef}
            </Typography>
            {(transaction.railRefs.RRN || transaction.railRefs.RaastMsgId) && <Typography variant="body2" color="text.secondary" data-id="element-2730" data-mp="3fkjshq2pyaeq">
                Rail Refs:{' '}
                {[transaction.railRefs.RaastMsgId && `Raast: ${transaction.railRefs.RaastMsgId}`, transaction.railRefs.RRN && `RRN: ${transaction.railRefs.RRN}`, transaction.railRefs.STAN && `STAN: ${transaction.railRefs.STAN}`, transaction.railRefs.UETR && `UETR: ${transaction.railRefs.UETR}`].filter(Boolean).join(' • ')}
              </Typography>}
          </Box>
        </Box>
        <Divider data-id="element-2731" data-mp="10ikmb1g5atoj" />
        {/* Accordion Content */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 3
      }} data-id="element-2732" data-mp="10jcj2flhaxge">
          {renderOverviewAccordion()}
          {renderPartiesAccordion()}
          {renderAccountsAccordion()}
          {renderAmountsAccordion()}
          {renderComplianceAccordion()}
          {renderTimelineAccordion()}
          {renderAmendmentHistoryAccordion()}
        </Box>
        {/* Action Buttons */}
        {renderActionButtons().length > 0 && <>
            <Divider data-id="element-2733" data-mp="2v4yyh8qeux4y" />
            <Box sx={{
          p: 3,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap'
        }} data-id="element-2734" data-mp="2q3rpj6cj7rwp">
              {renderActionButtons()}
            </Box>
          </>}
      </Box>
    </Drawer>;
};
export default TransactionDetailsDrawer;